import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg } from "@/lib/api";
import { POSITIONS } from "@/lib/dates";
import { availIssue } from "@/lib/availability";
import { AlertTriangle } from "lucide-react";

const EMPTY = { user_id: "", date: "", start: "16:00", end: "22:00", break_minutes: 30, position: "", note: "" };

export const ShiftDialog = ({ open, onOpenChange, initial, employees, availability = {}, onSaved }) => {
  const api = useApi();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const editing = !!initial?.id;

  useEffect(() => {
    if (open) setF({ ...EMPTY, ...initial });
  }, [open, initial]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const save = async () => {
    if (!f.user_id || !f.date) return toast.error("Kies een medewerker en datum");
    setBusy(true);
    const body = { ...f, break_minutes: Number(f.break_minutes) || 0, position: f.position === "none" ? "" : f.position };
    try {
      if (editing) await api.put(`/shifts/${initial.id}`, body);
      else await api.post("/shifts", body);
      toast.success(editing ? "Dienst bijgewerkt" : "Dienst toegevoegd");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/shifts/${initial.id}`);
      toast.success("Dienst verwijderd");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="shift-dialog">
        <DialogHeader><DialogTitle>{editing ? "Dienst wijzigen" : "Dienst toevoegen"}</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <div>
            <Label>Medewerker</Label>
            <Select value={f.user_id} onValueChange={set("user_id")}>
              <SelectTrigger className="mt-1.5" data-testid="shift-employee-select"><SelectValue placeholder="Kies medewerker" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => <SelectItem key={e.user_id} value={e.user_id} data-testid={`shift-employee-option-${e.user_id}`}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="shift-date">Datum</Label>
            <Input id="shift-date" type="date" value={f.date} onChange={set("date")} className="mt-1.5" data-testid="shift-date-input" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label htmlFor="shift-start">Begin</Label><Input id="shift-start" type="time" value={f.start} onChange={set("start")} className="mt-1.5" data-testid="shift-start-input" /></div>
            <div><Label htmlFor="shift-end">Einde</Label><Input id="shift-end" type="time" value={f.end} onChange={set("end")} className="mt-1.5" data-testid="shift-end-input" /></div>
            <div><Label htmlFor="shift-break">Pauze (min)</Label><Input id="shift-break" type="number" min="0" value={f.break_minutes} onChange={set("break_minutes")} className="mt-1.5" data-testid="shift-break-input" /></div>
          </div>
          <div>
            <Label>Functie</Label>
            <Select value={f.position || "none"} onValueChange={set("position")}>
              <SelectTrigger className="mt-1.5" data-testid="shift-position-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Geen</SelectItem>
                {POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="shift-note">Notitie</Label>
            <Input id="shift-note" value={f.note} onChange={set("note")} placeholder="Optioneel" className="mt-1.5" data-testid="shift-note-input" />
          </div>
        </div>
        {(() => {
          const issue = availIssue(availability[f.user_id], f.date, f.start, f.end);
          const name = employees.find((e) => e.user_id === f.user_id)?.name;
          return issue ? (
            <p className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="shift-availability-warning">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />{name} {issue}. Je kunt de dienst toch opslaan.
            </p>
          ) : null;
        })()}
        <DialogFooter className="gap-2 sm:justify-between">
          {editing ? <Button variant="ghost" className="text-red-600" onClick={remove} disabled={busy} data-testid="shift-delete-button">Verwijderen</Button> : <span />}
          <Button onClick={save} disabled={busy} data-testid="shift-save-button">Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
